import type { loader } from '../index.tsx'
import type { Strain } from '../../types.ts'
import { useLoaderData, Link, useParams } from '@remix-run/react'
import { cn } from '#app/utils/misc.tsx'
import { Button } from '#app/components/ui/button.tsx'
import { Icon } from '#app/components/ui/icon.tsx'
import { Badge } from '#app/components/ui/badge.tsx'

export default function ProductDetails() {
	const { product, children } = useLoaderData<typeof loader>()
	const { location } = useParams()

	//console.log(product);

	// build the link for the product identifier
	const productSlugOrId =
		product.leafProductMetadata?.slug ?? product.internalId

	//console.log(product.product_data_items);
	//console.log(product.strains);
	return (
		<div className="col-span-1 lg:col-span-5">
			<div className="space-y-4 px-2 pb-2 pt-4">
				<h1 className="text-2xl font-extrabold tracking-tight lg:text-3xl">
					{product.display_name}
				</h1>

				{product?.tagline && <p className="text-sm">{product?.tagline}</p>}

				<div className="space-x-2">
					<span
						className="prose text-sm"
						dangerouslySetInnerHTML={{
							__html: product?.description ?? '',
						}}
					/>
				</div>

				{children.length > 0 && (
					<div className="my-3 overflow-hidden">
						<div className="py-3">
							<h3 className="text-md font-medium">Varieties</h3>
						</div>

						<div className="flex flex-wrap gap-2">
							{children.map((child) => (
								<Badge
									className="bg-theme-blue hover:bg-theme-blue"
									key={child.id}
								>
									{child.name}
								</Badge>
							))}
						</div>
					</div>
				)}

				{product.product_data_items != null && (
					<div className="my-3 overflow-hidden">
						<div className="py-3">
							<h3 className="text-md font-medium">Product Information</h3>
						</div>

						<div className="border-t border-primary/20 py-2 sm:p-0">
							<dl className="divide-y divide-primary/20">
								<div className="py-2 sm:grid sm:grid-cols-3 sm:gap-4 sm:px-0">
									<dt className="text-sm font-medium">Category</dt>
									<dd className="mt-1 text-sm leading-6 sm:col-span-2 sm:mt-0">
										<Link
											to={
												`${location ? `/${location}` : ''}/products/?category=` +
												product?.leafProductCategory.slug
											}
										>
											<span className="bg-theme-blue hover:bg-theme-blue/90 rounded-md px-2 py-1 text-xs font-medium uppercase text-white">
												{product?.leafProductCategory.name}
											</span>
										</Link>
									</dd>
								</div>

								{product?.strains.length > 0 && (
									<>
										<div className="py-2 sm:grid sm:grid-cols-3 sm:gap-4">
											<dt className="0 text-sm font-medium">Strain</dt>
											<dd className="mt-1 text-sm capitalize sm:col-span-2 sm:mt-0">
												{(product.strains as Strain[]).map((strain, index) => (
													<span key={strain.id} className="text-sm">
														{(index ? ', ' : '') + strain.name}
													</span>
												))}
											</dd>
										</div>

										<div className="py-2 sm:grid sm:grid-cols-3 sm:gap-4">
											<dt className="text-sm font-medium">Classification</dt>
											<dd className="mt-1 text-sm capitalize sm:col-span-2 sm:mt-0">
												{(product.strains as Strain[]).map((strain, index) => (
													<span key={strain.id} className="text-sm">
														{(index ? ', ' : '') +
															(strain.strain_classification === 'na'
																? 'N/A'
																: strain.strain_classification)}
													</span>
												))}
											</dd>
										</div>
									</>
								)}
							</dl>
						</div>

						{(product.product_data_items as any[]).map((p, idx) => (
							<div key={idx} className="border-t border-primary/20 py-2 sm:p-0">
								<dl className="sm:divide-y sm:divide-primary/20">
									<div className="py-2 sm:grid sm:grid-cols-3 sm:gap-4">
										<dt
											className={cn(
												'0 text-sm font-medium',
												p.spec_type.length === 3 ? 'uppercase' : 'capitalize',
											)}
										>
											{p.spec_type}
										</dt>
										<dd className="mt-1 text-sm sm:col-span-2 sm:mt-0">
											{p.decimal_value} {p.dosage_unit}
										</dd>
									</div>
								</dl>
							</div>
						))}
					</div>
				)}
			</div>
			{/* <Button
				variant="secondary"
				asChild
				className="bg-theme-blue hover:bg-theme-blue/90 mt-8 w-full rounded-full px-8 py-10 text-lg font-bold text-white transition-all duration-300 ease-in-out hover:shadow-lg"
			>
				<Link
					to={`${location ? `/${location}` : ''}/dispensaries/?product=${productSlugOrId}`}
				>
					Find in Dispensary
				</Link>
			</Button> */}
		</div>
	)
}
